import { Injectable, inject, signal, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { AiAssistantService, InvokeAgentCoreRequest, InvokeAgentCoreResponse } from '../../services/ai-assistant.service';
import { GetLocalStorageValues } from '../../util/local-storage';
import { getModuleFromUrl } from '../../util/route-helper';

export interface HelpPanelCache {
  summary: string;
  messages: { type: 'user' | 'ai'; content: string }[];
  sessionId: string;
}

@Injectable({ providedIn: 'root' })
export class HelpPanelService {
  private readonly router = inject(Router);
  private readonly aiService = inject(AiAssistantService);
  private readonly localStorage = new GetLocalStorageValues();

  // State
  readonly isOpen = signal(this.loadOpenState());
  readonly currentRoute = signal('');
  readonly currentModule = signal(0);
  readonly isLoading = signal(false);
  readonly summary = signal('');
  readonly messages = signal<{ type: 'user' | 'ai'; content: string }[]>([]);
  readonly panelWidth = signal(this.loadWidth());
  readonly error = signal('');

  // Cache per route
  private cache = new Map<string, HelpPanelCache>();
  private sessionId = '';

  constructor() {
    // Set initial route
    const initialRoute = this.router.url.split('?')[0];
    this.currentRoute.set(initialRoute);
    this.currentModule.set(getModuleFromUrl(initialRoute));

    // If panel was left open, fetch summary for current page
    if (this.isOpen() && initialRoute) {
      setTimeout(() => this.fetchSummary());
    }

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      const route = e.urlAfterRedirects.split('?')[0];
      if (route !== this.currentRoute()) {
        this.currentRoute.set(route);
        this.currentModule.set(getModuleFromUrl(route));
        this.onRouteChange(route);
      }
    });
  }

  toggle(): void {
    const next = !this.isOpen();
    this.isOpen.set(next);
    window.localStorage.setItem('help_panel_open', JSON.stringify(next));
    if (next && !this.summary() && this.currentRoute()) {
      this.fetchSummary();
    }
  }

  close(): void {
    this.isOpen.set(false);
    window.localStorage.setItem('help_panel_open', 'false');
  }

  setWidth(w: number): void {
    this.panelWidth.set(w);
    window.localStorage.setItem('help_panel_width', String(w));
  }

  sendMessage(text: string): void {
    if (!text.trim()) return;
    const msgs = [...this.messages(), { type: 'user' as const, content: text }];
    this.messages.set(msgs);
    this.isLoading.set(true);

    const req = this.buildRequest(text);
    this.aiService.invokeAgentCore(req).subscribe({
      next: (res: InvokeAgentCoreResponse) => {
        if (res.sessionId) this.sessionId = res.sessionId;
        const parsed = JSON.parse(res.response);
        const content = parsed.result.content.map((c: any) => c.text).join('\n');
        const updated = [...this.messages(), { type: 'ai' as const, content }];
        this.messages.set(updated);
        this.isLoading.set(false);
        this.updateCache();
      },
      error: () => {
        const updated = [...this.messages(), { type: 'ai' as const, content: 'Sorry, I encountered an error. Please try again.' }];
        this.messages.set(updated);
        this.isLoading.set(false);
      },
    });
  }

  fetchSummary(): void {
    if (this.isLoading()) return;
    // Ensure we have the current route
    const route = this.router.url.split('?')[0];
    if (route && route !== '/') {
      this.currentRoute.set(route);
      this.currentModule.set(getModuleFromUrl(route));
    }
    this.isLoading.set(true);
    this.error.set('');
    const prompt = `You are a help assistant for ActivityPro Web Admin. The user is on the "${this.getPageName()}" page. Provide a brief 2-3 sentence summary of what the user can do on this page, followed by 3-5 key capabilities as bullet points. Keep it concise and actionable.`;
    const req = this.buildRequest(prompt);

    this.aiService.invokeAgentCore(req).subscribe({
      next: (res: InvokeAgentCoreResponse) => {
        if (res.sessionId) this.sessionId = res.sessionId;
        const parsed = JSON.parse(res.response);
        const content = parsed.result.content.map((c: any) => c.text).join('\n');
        this.summary.set(content);
        this.isLoading.set(false);
        this.updateCache();
      },
      error: () => {
        this.error.set('Failed to load help content');
        this.isLoading.set(false);
      },
    });
  }

  private onRouteChange(route: string): void {
    const cached = this.cache.get(route);
    if (cached) {
      this.summary.set(cached.summary);
      this.messages.set(cached.messages);
      this.sessionId = cached.sessionId;
    } else {
      this.summary.set('');
      this.messages.set([]);
      this.sessionId = '';
      if (this.isOpen()) this.fetchSummary();
    }
  }

  private updateCache(): void {
    this.cache.set(this.currentRoute(), {
      summary: this.summary(),
      messages: this.messages(),
      sessionId: this.sessionId,
    });
  }

  private buildRequest(prompt: string): InvokeAgentCoreRequest {
    return {
      parentclub_id: this.localStorage.getPostgreParentClubId(),
      club_id: '',
      member_id: '',
      prompt,
      locale: 'UK',
      limit: 10,
      start_date: '',
      end_date: '',
      apptype: 10,
      module: this.currentModule(),
      session_id: this.sessionId,
    };
  }

  private getPageName(): string {
    const route = this.currentRoute();
    const segments = route.split('/').filter(Boolean);
    // Return last meaningful segment
    const last = segments[segments.length - 1] || 'Dashboard';
    return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
  }

  private loadOpenState(): boolean {
    try { return JSON.parse(window.localStorage.getItem('help_panel_open') || 'false'); }
    catch { return false; }
  }

  private loadWidth(): number {
    const w = parseInt(window.localStorage.getItem('help_panel_width') || '320', 10);
    return w >= 280 && w <= 600 ? w : 320;
  }
}
